<template>
  <b-card tag="article" class="mb-3">
    <h4 class="card-title title-slash">
      {{ $t('dashboard.pendingInvoicesWidget.title') }}
    </h4>
    <template v-if="!loading && pendingInvoices.length > 0">
      <b-table
        responsive
        :items="pendingInvoices"
        :fields="invoiceTableFields"
        class="table-clean"
      >
        <template v-slot:cell(value)="data">
          {{ data.value | currency(data.item.currsym, null, 0) }}
        </template>
        <template v-slot:cell(reference)="data">
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : { name: 'invoicesAdd', params: { id: data.item.id } }
            "
            v-track-click
            v-if="isDraft(data.item.stage_human)"
            :id="`pending-invoices-edit-ref-${data.item.reference}`"
          >
            {{ data.item.reference }}
          </router-link>
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : {
                    name: 'invoicesAdd',
                    params: { id: data.item.id, value: 'upload-docs' }
                  }
            "
            v-track-click
            v-else-if="isDocsPending(data.item.stage_human)"
            :id="`pending-invoices-edit-ref-${data.item.reference}`"
          >
            {{ data.item.reference }}
          </router-link>
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : {
                    name: 'invoicesAdd',
                    params: { id: data.item.id, value: 'review' }
                  }
            "
            v-else-if="isReviewPending(data.item.stage_human)"
            :id="`pending-invoices-edit-ref-${data.item.reference}`"
          >
            {{ data.item.reference }}
          </router-link>
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : { name: 'invoicesSingle', params: { id: data.item.id } }
            "
            v-else
            v-track-click
            :id="`pending-invoices-view-ref-${data.item.reference}`"
          >
            {{ data.item.reference }}
          </router-link>
        </template>
        <template v-slot:cell(stage_human)="data">
          <div
            class="invoice-status"
            :class="data.item.stage_human | invoiceStageClass"
          >
            {{
              $t(
                `dashboard.pendingInvoicesWidget.status['${data.item.stage_human}']`
              )
            }}
          </div>
        </template>
        <template v-slot:cell(actions)="data">
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : { name: 'invoicesAdd', params: { id: data.item.id } }
            "
            v-track-click
            v-if="isDraft(data.item.stage_human)"
            :id="`pending-invoices-edit-${data.item.reference}`"
          >
            {{ $t('dashboard.pendingInvoicesWidget.edit') }}
          </router-link>
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : {
                    name: 'invoicesAdd',
                    params: { id: data.item.id, value: 'upload-docs' }
                  }
            "
            v-track-click
            v-else-if="isDocsPending(data.item.stage_human)"
            :id="`pending-invoices-edit-${data.item.reference}`"
          >
            {{ $t('dashboard.pendingInvoicesWidget.edit') }}
          </router-link>
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : {
                    name: 'invoicesAdd',
                    params: { id: data.item.id, value: 'review' }
                  }
            "
            v-track-click
            v-else-if="isReviewPending(data.item.stage_human)"
            :id="`pending-invoices-edit-${data.item.reference}`"
          >
            {{ $t('dashboard.pendingInvoicesWidget.edit') }}
          </router-link>
          <router-link
            :to="
              newInvoiceRedirect
                ? { name: 'account' }
                : { name: 'invoicesSingle', params: { id: data.item.id } }
            "
            v-track-click
            v-else
            :id="`pending-invoices-view-${data.item.id}`"
          >
            {{ $t('dashboard.pendingInvoicesWidget.view') }}
          </router-link>
        </template>
      </b-table>
      <div class="card-actions">
        <router-link
          :to="{ name: 'invoices', params: { selectedFilter: 'pending' } }"
          slot="footer"
          v-track-click
          id="pending-invoices-all"
        >
          {{ $t('dashboard.pendingInvoicesWidget.viewAll') }}
        </router-link>
      </div>
    </template>
    <template v-else-if="!loading && pendingInvoices.length === 0">
      <p class="lead">
        {{ $t('dashboard.pendingInvoicesWidget.noPending') }}
        <router-link
          :to="
            newInvoiceRedirect ? { name: 'account' } : { name: 'invoicesAdd' }
          "
          v-track-click
          id="pending-invoices-add"
          >{{ $t('dashboard.pendingInvoicesWidget.here') }}</router-link
        >
        {{ $t('dashboard.pendingInvoicesWidget.addNew') }}
      </p>
      <div class="card-actions">
        <router-link
          :to="{ name: 'invoices' }"
          slot="footer"
          v-track-click
          id="pending-invoices-view-all-empty"
        >
          {{ $t('dashboard.pendingInvoicesWidget.viewAll') }}
        </router-link>
      </div>
    </template>
    <div v-else>
      <loader :height="268" />
    </div>
  </b-card>
</template>
<script>
import Loader from '@/components/common/Loader'
import exportersService from '@/services/exporters'

export default {
  props: ['monitoringData'],
  data() {
    return {
      loading: true,
      pendingInvoices: []
    }
  },
  computed: {
    invoiceTableFields() {
      return this.$t('dashboard.pendingInvoicesWidget.invoiceTableFields')
    },
    newInvoiceRedirect() {
      if (
        !!this.monitoringData &&
        !!this.monitoringData.mail_date &&
        this.$moment(this.monitoringData.revoke_date).isValid() &&
        this.$moment(this.monitoringData.mail_date).isValid() &&
        (!this.monitoringData.financials.active ||
          !this.monitoringData.gst.active)
      ) {
        const mailDate = new Date(this.monitoringData.mail_date) // Convert to Date object
        const currentDate = new Date() // Get today's date

        // Calculate the date 45 days after the mail_date
        const days60AfterMailDate = new Date(mailDate)
        days60AfterMailDate.setDate(mailDate.getDate() + 60)

        return currentDate >= new Date(this.monitoringData.revoke_date)
      }
      return false
    }
  },
  mounted() {
    this.fetchPendingInvoices()
  },
  methods: {
    async fetchPendingInvoices() {
      this.loading = true
      exportersService
        .fetchPendingInvoices(
          this.$store.state.account.accountId,
          {
            limit: 5
          },
          true
        )
        .then((res) => {
          this.loading = false
          this.pendingInvoices = res.data['drip/business/invoices']
        })
        .catch(() => {
          this.loading = false
        })
    },
    isDraft(stage) {
      return [
        this.$t(`invoiceTableItems.status.Draft`),
        this.$t(`invoiceTableItems.status['Basic Info Pending']`)
      ].includes(stage)
    },
    isDocsPending(stage) {
      return stage === this.$t(`invoiceTableItems.status['Docs Pending']`)
    },
    isReviewPending(stage) {
      return stage === this.$store.state.constants.invoiceStage.review
    }
  },
  components: {
    Loader
  }
}
</script>
